/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FaSyncAlt } from 'react-icons/fa';

import { SmallButton } from '../../../../../styles/ui/Button';
import {
   generateChecklist,
   updateDocument,
   uploadDocumentFile,
   getDocumentDownload,
} from '../../../../../services/hr/recruitment/admissionDocumentsApi';
import {
   ADMISSION_DOCUMENT_STATUS,
   mandatoryAdmissionDocRank,
} from '../../constants';
import { recruitmentErrorMessage } from '../../utils/apiError';
import {
   Card,
   CardHead,
   InlineHint,
   DocGroup,
   DocList,
   DocProgress,
   SectionSub,
} from '../styles';
import DocumentRow from './DocumentRow';

// ---------------------------------------------------------------------------
// Checklist de documentos de admissão. Gerado/regenerado pelo BACKEND (E2
// aprovado, pré-cadastro, dependentes); "Recalcular" força o recálculo
// (idempotente — só adiciona faltantes). Agrupado: documentos do candidato ×
// documentos por dependente. As mutações (upload/valor/status) vivem
// aqui — `busyDocumentId` único evita duplo-submit por linha.
// ---------------------------------------------------------------------------

// Recebidos = RECEBIDO ou ENVIADO_CONTABILIDADE; NAO_APLICA sai do denominador
// (aviso não-bloqueante não conta como faltante — mesma semântica H.3.1).
function checklistProgress(documents) {
   const relevant = documents.filter(
      (doc) => doc.status !== ADMISSION_DOCUMENT_STATUS.NAO_APLICA,
   );
   const received = relevant.filter(
      (doc) =>
         doc.status === ADMISSION_DOCUMENT_STATUS.RECEBIDO ||
         doc.status === ADMISSION_DOCUMENT_STATUS.ENVIADO_CONTABILIDADE,
   );
   return { total: relevant.length, received: received.length };
}

export default function DocumentsChecklistCard({
   candidateId,
   documents = [],
   dependents = [],
   canManage,
   isReadOnly,
   onChanged,
}) {
   const [busyDocumentId, setBusyDocumentId] = useState(null);
   const [isGenerating, setIsGenerating] = useState(false);

   const canAct = canManage && !isReadOnly;

   const groups = useMemo(() => {
      const dependentNameById = {};
      dependents.forEach((dependent) => {
         dependentNameById[String(dependent.id)] = dependent.full_name;
      });

      // Obrigatórios no topo, na ordem canônica; opcionais depois, mantendo a
      // ordem do backend (sort estável).
      const candidateDocs = documents
         .filter((doc) => !doc.dependent_id)
         .sort((a, b) => {
            const rankA = mandatoryAdmissionDocRank(a.document_type);
            const rankB = mandatoryAdmissionDocRank(b.document_type);
            return (
               (rankA === -1 ? Number.MAX_SAFE_INTEGER : rankA) -
               (rankB === -1 ? Number.MAX_SAFE_INTEGER : rankB)
            );
         });
      const byDependent = new Map();
      documents
         .filter((doc) => doc.dependent_id)
         .forEach((doc) => {
            const key = String(doc.dependent_id);
            if (!byDependent.has(key)) byDependent.set(key, []);
            byDependent.get(key).push(doc);
         });

      return {
         candidateDocs,
         dependentGroups: [...byDependent.entries()].map(([id, docs]) => ({
            id,
            name:
               docs[0]?.dependent?.full_name ??
               dependentNameById[id] ??
               'Dependente',
            docs,
         })),
      };
   }, [documents, dependents]);

   const progress = checklistProgress(documents);

   const runDocumentAction = async (doc, action, errorFallback) => {
      if (busyDocumentId) return;
      setBusyDocumentId(doc.id);
      try {
         await action();
         onChanged?.();
      } catch (err) {
         toast.error(recruitmentErrorMessage(err, errorFallback));
      } finally {
         setBusyDocumentId(null);
      }
   };

   const handleUploadFile = (doc, file) =>
      runDocumentAction(
         doc,
         async () => {
            const { message } = await uploadDocumentFile(doc.id, file);
            toast.success(message);
         },
         'Erro ao enviar o arquivo.',
      );

   const handleSaveDetail = (doc, patch) => {
      if (!Object.keys(patch).length) return;
      runDocumentAction(
         doc,
         async () => {
            const { message } = await updateDocument(doc.id, patch);
            toast.success(message);
         },
         'Erro ao atualizar o documento.',
      );
   };

   const handleSetStatus = (doc, status) =>
      runDocumentAction(
         doc,
         async () => {
            const { message } = await updateDocument(doc.id, { status });
            toast.success(message);
         },
         'Erro ao atualizar o status do documento.',
      );

   // Download não muda estado — não passa pelo onChanged.
   const handleDownload = async (doc) => {
      try {
         const { url } = await getDocumentDownload(doc.id);
         if (!url) {
            toast.error('Não foi possível gerar o link de download.');
            return;
         }
         window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao gerar o link de download.'),
         );
      }
   };

   const handleGenerate = async () => {
      if (isGenerating) return;
      setIsGenerating(true);
      try {
         const { message } = await generateChecklist(candidateId);
         toast.success(message);
         onChanged?.();
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao recalcular o checklist.'),
         );
      } finally {
         setIsGenerating(false);
      }
   };

   const renderRows = (docs) => (
      <DocList>
         {docs.map((doc) => (
            <DocumentRow
               key={doc.id}
               document={doc}
               canAct={canAct}
               isBusy={busyDocumentId === doc.id}
               onUploadFile={handleUploadFile}
               onSaveDetail={handleSaveDetail}
               onSetStatus={handleSetStatus}
               onDownload={handleDownload}
            />
         ))}
      </DocList>
   );

   const progressPercent = progress.total
      ? Math.round((progress.received / progress.total) * 100)
      : 0;

   return (
      <Card>
         <CardHead>
            <h2>
               Documentos do candidato
               <SectionSub>
                  {progress.received} de {progress.total} recebidos
               </SectionSub>
            </h2>
            {canAct && (
               <SmallButton
                  type="button"
                  $intent="write"
                  onClick={handleGenerate}
                  disabled={isGenerating || Boolean(busyDocumentId)}
               >
                  <FaSyncAlt size={11} />{' '}
                  {isGenerating ? 'Recalculando…' : 'Recalcular checklist'}
               </SmallButton>
            )}
         </CardHead>

         {documents.length > 0 && (
            <DocProgress>
               <i style={{ width: `${progressPercent}%` }} />
            </DocProgress>
         )}

         {documents.length === 0 ? (
            <InlineHint>
               Nenhum documento no checklist. Use &quot;Recalcular
               checklist&quot; para gerá-lo a partir das regras (sexo, estado
               civil, dependentes e cargo).
            </InlineHint>
         ) : (
            <>
               {groups.candidateDocs.length > 0 && (
                  <DocGroup>
                     <div className="group-title">Documentos do candidato</div>
                     {renderRows(groups.candidateDocs)}
                  </DocGroup>
               )}
               {groups.dependentGroups.map((group) => (
                  <DocGroup key={group.id}>
                     <div className="group-title">
                        Dependente · {group.name}
                     </div>
                     {renderRows(group.docs)}
                  </DocGroup>
               ))}
            </>
         )}
      </Card>
   );
}

DocumentsChecklistCard.propTypes = {
   candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   documents: PropTypes.arrayOf(PropTypes.shape({})),
   dependents: PropTypes.arrayOf(PropTypes.shape({})),
   canManage: PropTypes.bool,
   isReadOnly: PropTypes.bool,
   onChanged: PropTypes.func,
};
